import { CoreStore } from '../../../../core/store/core.store';
import { PasswordChangeComponent } from '../../../../shared/components/password-change/password-change.component';
import { LoginService, WelcomeError } from '../../services/login.service';
import { getUser, getWelcomeScreenState } from '../../store/login.selectors';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { TFormBuilder } from '@ppl/ui/form';
import { markAllControlsAsDirty } from '@ppl/utils/form.utils';
import { BehaviorSubject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import type {
  UntypedFormGroup
} from '@angular/forms';
import type { TControlsConfig, TFormGroup } from '@ppl/ui/form';
import type { PasswordChangeFormData, PasswordFormData } from '../../../../shared/components/password-change/password-change.component';
import type { UserData, WelcomeErrorInfo } from '../../services/login.service';
import type { User, WelcomeScreenState } from '../../store/login.reducer';

@Component({
    selector: 'ppl-welcome-screen',
    templateUrl: './welcome-screen.component.html',
    styleUrls: ['./welcome-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WelcomeScreenComponent {
  saving$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<WelcomeErrorInfo>(null);
  WelcomeError = WelcomeError;

  form: TFormGroup<WelcomeScreenFormData>;

  user: User;
  settings: WelcomeScreenState;

  changePassword = false;

  get canChangePassword() {
    return this.settings.can_change_password;
  }

  get mustChangePassword() {
    // NOTE(mike): Decides if this is a invite or not.
    return !!this.settings.must_change_password;
  }

  constructor(
    private store: CoreStore,
    private fb: TFormBuilder,
    private loginService: LoginService
  ) {
    // NOTE(mike): These initializations must be first, because
    // for example `createForm` depends on them.
    this.user = this.store.get(getUser);
    this.settings = this.store.get(getWelcomeScreenState);
    this.form = this.createForm(this.user);
  }

  createForm(user: User) {
    const commonControlsConfig: TControlsConfig<Omit<WelcomeScreenFormData, 'passwords'>> = {
      email: [{ value: user.email, disabled: true }, [Validators.required]],
      first_name: [user.first_name, Validators.required],
      last_name: [user.last_name, Validators.required],
      terms_and_conditions: [false, Validators.requiredTrue]
    };

    const passwordsControlsConfig: TControlsConfig<Pick<WelcomeScreenFormData, 'passwords'>> = {};
    if (this.canChangePassword) {
      if (this.mustChangePassword) {
        passwordsControlsConfig.passwords = PasswordChangeComponent.createPasswordForm();
      } else {
        const passwords = PasswordChangeComponent.createChangePasswordForm();
        passwordsControlsConfig.passwords = passwords;
        this.toggleChangePassword(passwords);
      }
    }

    return this.fb.group<WelcomeScreenFormData>({
      ...commonControlsConfig,
      ...passwordsControlsConfig
    });
  }

  toggleChangePassword(passwords: UntypedFormGroup) {
    this.changePassword
      ? passwords.enable()
      : passwords.disable();
  }

  onToggleChangePassword() {
    this.changePassword = !this.changePassword;
    this.toggleChangePassword(this.form.get('passwords') as UntypedFormGroup);
  }

  onSaveAndContinue() {
    if (this.form.valid) {
      const { first_name, last_name, terms_and_conditions, passwords } = this.form.value;
      const userData: UserData = {
        first_name,
        last_name,
        terms_and_conditions
      };

      if (passwords) {
        const { password1 } = passwords;
        const currentPassword = (passwords as PasswordChangeFormData).currentPassword;
        userData.password = password1;
        if (currentPassword) {
          userData.old_password = currentPassword;
        }
      }

      this.saving$.next(true);
      this.loginService.updateUserData(userData).pipe(
        finalize(() => this.saving$.next(false))
      ).subscribe({
        error: (error: WelcomeErrorInfo) => {
          this.error$.next(error);
        }
      });
    } else {
      markAllControlsAsDirty(this.form);
    }
  }
}

interface WelcomeScreenFormData {
  email: string;
  first_name: string;
  last_name: string;
  terms_and_conditions: boolean;
  passwords?: PasswordFormData | PasswordChangeFormData;
}
